import React, { useEffect } from 'react';
import { Input } from 'reactstrap';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import withLoader from 'components/organisms/withLoader';
import {
  API_PATIENT_DIAGNOSES,
  API_UPDATE_PATIENT_DIAGNOSES,
} from 'services/dataService/ccmResources';
import AutoCompleteClinics from 'components/molecules/autocompleteFilter/icdCodes';
import Pagination from 'components/molecules/paginationTransition';
import useTable from 'utils/useTable';
import useValidation from 'utils/useValidation';
import common from 'constants/common';
import useEditingTable from 'utils/useEditingTable';
import { showResult } from 'utils';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import classNames from 'classnames';
import TableEditing from 'components/molecules/tableEditing/index';
import PropTypes from 'prop-types';
import TableActions from '../components/tableActions';

const updatePresenter = ({ guid, diagnoseGuid, description, patientGuid }) => ({
  guid,
  diagnoseGuid,
  description,
  patientGuid,
});

const VALIDATION_RULES = Yup.object().shape({
  diagnoseGuid: Yup.string().required('ICD-10 Code is a required field'),
  description: Yup.string().required(),
});

const validateAndSaveChanges =
  (validateRow, saveData, reloader) => async () => {
    const result = await validateRow();
    if (result.isValid) {
      const { error } = await saveData();
      if (error) showResult(error);
      else showResult();
      reloader();
    }
  };
const PER_PAGE = 5;
const createRow = () => ({
  diagnoseGuid: '',
  description: '',
});

const TABLE_ROW = {
  guid: {
    name: 'guid',
    visible: false,
    disabled: false,
  },
  diagnoseGuid: {
    name: 'diagnoseGuid',
    visible: false,
    disabled: false,
  },
  description: {
    name: 'description',
    placeholder: 'Description',
    disabled: false,
    visible: true,
    component: 'textarea',
  },
  code: { name: 'code', placeholder: 'Code', disabled: true, visible: true },
  createdAt: {
    name: 'createdAt',
    placeholder: 'createdAt',
    disabled: true,
    visible: true,
  },
};
const DATE_FORMAT = common.dateFormats.full;

const Conditions = ({
  isAdminPanel = false,
  makeInitialRequest = false,
  canEditPage,
}) => {
  const { patientGuid } = useParams();
  const dates = useSelector((state) => state.ccmPatientDetails.dates);
  const {
    row: editRow,
    rowIndex,
    isEditing,
    loading: editLoading,
    isValid: isValidEditing,
    messageErrors: editMessageErrors,
    setRow: setEditingRow,
    startEditing,
    closeEditing,
    validate: validateEditingRow,
  } = useEditingTable(VALIDATION_RULES);

  const { row, setRow, isValid, validate, messageErrors } = useValidation(
    {},
    VALIDATION_RULES,
  );
  const icdCodeChanged = ({ value, description }) => {
    if (!value) {
      return setRow({
        ...row,
        diagnoseGuid: value,
        description: '',
      });
    }
    setRow({
      ...row,
      diagnoseGuid: value,
      description: `${row.description || ''} ${description || ''}`.trim(),
    });
  };

  const createNewRow = () => {
    setRow(createRow());
  };
  const clearRows = () => {
    setRow({});
  };
  const {
    data: { items },
    count,
    loading,
    saveLoading,
    page,
    setPage,
    setFilters,
    saveNewRow,
    updateRow,
    deleteRowWithBody,
  } = useTable(
    API_PATIENT_DIAGNOSES,
    PER_PAGE,
    { patientGuid },
    { makeInitialRequest },
  );

  const rowIsSaved = !Object.keys(row).length;
  const rowEditingSaved = !Object.keys(editRow).length;

  const deleteTableRow = async (guid) => {
    const path = `${API_PATIENT_DIAGNOSES}/${guid}`;
    const body = {
      patientGuid,
    };
    const { error } = await deleteRowWithBody(path, body);
    showResult(error, common.submitSuccessTypes.changed);
  };

  useEffect(() => {
    if (dates.length > 1) {
      const startTime = format(dates[0], DATE_FORMAT);
      const endTime = format(dates[1], DATE_FORMAT);
      setFilters({ patientGuid, startTime, endTime });
      clearRows();
    }
  }, [dates, patientGuid]);

  return (
    <>
      {withLoader(
        loading,
        <>
          <h3 className="ccm-table-section__h4">Conditions</h3>
          <div className="table-outer table-custom-scrollbar">
            <table cellSpacing="0" cellPadding="0" className="table-border">
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>ICD-10 Code</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {!!items.length &&
                  items.map((el, index) => (
                    <TableEditing
                      key={index}
                      rowIndex={index}
                      isEditing={index === rowIndex && isEditing}
                      setRow={setEditingRow}
                      isValid={isValidEditing}
                      editingRow={editRow}
                      rowData={[
                        { ...TABLE_ROW.guid, value: el.guid },
                        { ...TABLE_ROW.diagnoseGuid, value: el.icdCode.guid },
                        {
                          ...TABLE_ROW.createdAt,
                          value: format(new Date(el.createdAt), DATE_FORMAT),
                        },
                        {
                          ...TABLE_ROW.code,
                          value: el.icdCode.code,
                          error: editMessageErrors.code,
                        },
                        {
                          ...TABLE_ROW.description,
                          value: el.description,
                          error: editMessageErrors.description,
                        },
                      ]}
                      author={{
                        author: el.author,
                        date: el.updatedAt,
                      }}
                      startEditing={startEditing}
                      onDelete={() => deleteTableRow(el.guid)}
                      canEditPage={canEditPage}
                    />
                  ))}

                {!rowIsSaved && !isEditing && (
                  <tr>
                    <td style={{ width: 'unset' }} />
                    <td
                      className={classNames({
                        'table-column__input': true,
                      })}
                      style={{ padding: '0.5rem', width: 'unset' }}
                    >
                      <AutoCompleteClinics
                        filter={{
                          placeholder: 'Code',
                          label: 'Code',
                          id: 'code',
                        }}
                        handleClick={icdCodeChanged}
                      />
                      {messageErrors.diagnoseGuid ? (
                        <div className="table-column__error">
                          {messageErrors.diagnoseGuid}
                        </div>
                      ) : null}
                    </td>
                    <td
                      colSpan="2"
                      className={classNames({
                        'table-column__input': true,
                        'table-column__input--error':
                          row.description && !isValid,
                      })}
                      style={{ width: 'unset' }}
                    >
                      <div>
                        <div className="table-column__input-container">
                          <Input
                            type="textarea"
                            value={row.description}
                            defaultValue={row.description}
                            onChange={(e) =>
                              setRow({ ...row, description: e.target.value })
                            }
                          />
                        </div>

                        {messageErrors.description ? (
                          <div className="table-column__error">
                            {messageErrors.description}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!saveLoading && !isEditing && (
            <TableActions
              save={validateAndSaveChanges(
                validate,
                () =>
                  saveNewRow(API_PATIENT_DIAGNOSES, {
                    ...row,
                    patientGuid,
                  }),

                clearRows,
              )}
              cancel={clearRows}
              new={createNewRow}
              rowIsSaved={rowIsSaved}
              isAdminPanel={isAdminPanel}
            />
          )}
          {!editLoading && isEditing && (
            <TableActions
              save={validateAndSaveChanges(
                validateEditingRow,
                () =>
                  updateRow(
                    API_UPDATE_PATIENT_DIAGNOSES(patientGuid),
                    updatePresenter({
                      ...editRow,
                      patientGuid,
                    }),
                  ),
                closeEditing,
              )}
              cancel={closeEditing}
              rowIsSaved={rowEditingSaved}
            />
          )}

          <div className="ccm-table-section__pagination">
            <Pagination
              title=""
              page={page}
              pageTotal={items.length}
              itemsPerPage={PER_PAGE}
              total={count}
              handleFirstClick={() => setPage(1)}
              handleLastClick={() => setPage(Math.ceil(count / PER_PAGE))}
              handlePrevClick={() => setPage(page - 1)}
              handleNextClick={() => setPage(page + 1)}
              handleGoToPage={(numberPage) => setPage(numberPage)}
            />
          </div>
        </>,
      )}
    </>
  );
};
Conditions.propTypes = {
  makeInitialRequest: PropTypes.bool,
  isAdminPanel: PropTypes.bool,
  canEditPage: PropTypes.bool,
};
export default Conditions;
